import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import get from 'lodash/get';

import { AccountType, getAccount, getAccountType } from 'modules/accounts';
import { loadTalent, getTalentById } from 'modules/talents';
import { loadAudition, getAuditionById } from 'modules/auditions';
import {
  setAuditionApplicationUnreadStatus,
  getApplicationByTalentAndAuditionId,
} from 'modules/audition-applications';
import {
  createAuditionMessage,
  getExchangedMessages,
} from 'modules/audition-messages';
import { createAudioClip } from 'modules/audio-clips';
import { ChatWindow } from 'modules/chat';

import { loadAuditionApplicationMessaging } from '../redux';

function AuditionApplicationChatWindow({
  auditionId,
  talentId,
  options,
  ...otherProps
}) {
  const dispatch = useDispatch();

  const audition = useSelector(getAuditionById(auditionId));
  const auditionName = get(audition, 'name');

  const talent = useSelector(getTalentById(talentId));

  const producer = get(audition, 'producer');
  const producerId = get(producer, 'id');

  const account = useSelector(getAccount);
  const accountId = get(account, 'id');
  const accountType = useSelector(getAccountType);

  const chattingTo = AccountType.isTalent(accountType) ? producer : talent;
  const chattingWith = get(chattingTo, 'fullName');
  const profileImage = get(chattingTo, 'profileImage');

  const application = useSelector(
    getApplicationByTalentAndAuditionId(talentId, auditionId),
  );
  const applicationId = get(application, 'id');

  const messages = useSelector(
    getExchangedMessages(talentId, producerId, auditionId),
  );

  const [loadingMessages, setLoadingMessages] = useState(true);

  useEffect(() => {
    Promise.all([
      dispatch(loadTalent(talentId)),
      dispatch(loadAudition(auditionId)),
      dispatch(
        loadAuditionApplicationMessaging(accountId, auditionId, talentId),
      ),
    ]).then(() => setLoadingMessages(false));
  }, [dispatch, talentId, auditionId, accountId]);

  async function handleSubmitClick(data) {
    const file = get(data, 'file');
    const message = get(data, 'text');

    if (message === undefined) {
      return Promise.reject();
    }

    const messagePayload = await dispatch(
      createAuditionMessage({
        auditionId,
        applicationId,
        authorId: get(producer, 'accountId'),
        message,
      }),
    );

    await dispatch(
      setAuditionApplicationUnreadStatus({
        auditionId,
        applicationId,
        unreadMessageForAccountId: null, // This is PRD behavior even though it feels wrong
      }),
    );

    const messageId = get(messagePayload, 'payload.data.id');

    if (!messageId) {
      //TODO: not sure if this should be rejected or not
      return Promise.resolve();
    }

    if (file) {
      return dispatch(
        createAudioClip({
          auditionId: audition.id,
          applicationId: application.id,
          messageId,
          values: data,
        }),
      ).then(result => {
        return result;
      });
    }

    return Promise.resolve();
  }

  return (
    <ChatWindow
      headingTitle={auditionName}
      headingSubtitle={chattingWith}
      photoUrl={profileImage}
      messages={messages}
      onSubmitClick={handleSubmitClick}
      isLoadingMessages={loadingMessages}
      options={options}
      enableSampleUploading
      {...otherProps}
    />
  );
}

AuditionApplicationChatWindow.propTypes = {
  auditionId: PropTypes.string.isRequired,
  talentId: PropTypes.string.isRequired,
  className: PropTypes.string,
  options: PropTypes.node,
};

export default AuditionApplicationChatWindow;
